import { z } from "zod";
import { getToken } from "../auth/getToken";
import { appConfig } from "../appConfig";
import { logError } from "./logError";
import { getCurrentOrganisation } from "../root/CurrentOrganisationProvider";
import { AUTH_ERROR_CODES } from "../auth/AuthProvider";

type CustomHeaders = { "Draftit-Organisation-Id"?: string };

type CustomOptions = { excludeCurrentOrganisationId?: true; excludeToken?: true };
type BaseClientOptions = RequestInit & CustomOptions;

const baseClient = async (
  endpoint: string,
  { headers, excludeCurrentOrganisationId, excludeToken, ...options }: BaseClientOptions
): Promise<unknown> => {
  const token = excludeToken ? undefined : getToken();
  if (!excludeToken && !token) return Promise.reject({ errorCode: AUTH_ERROR_CODES.NO_TOKEN });

  const customHeaders: CustomHeaders = {};
  if (!excludeCurrentOrganisationId) {
    const currentOrganisation = await getCurrentOrganisation();

    if (!currentOrganisation) return Promise.reject({ errorCode: "NO_CURRENT_ORGANIZATION" });
    customHeaders["Draftit-Organisation-Id"] = currentOrganisation.id;
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: { ...headers, ...customHeaders, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });

  if (response.status === 401) return Promise.reject({ errorCode: AUTH_ERROR_CODES.NOT_AUTHORIZED });

  if (response.ok) {
    const contentType = response.headers.get("Content-Type");
    if (contentType?.includes("application/json")) return response.json().catch(() => {});
    return response;
  }

  const errorResponse = await response.json().catch(() => ({}));
  const { status, statusText } = response;

  const stack = `${options.method}: ${endpoint}`;

  return Promise.reject({ status, statusText, stack, ...errorResponse });
};

const baseClientWithSchema = async <Schema extends z.ZodTypeAny>(
  endpoint: string,
  options: BaseClientOptions,
  responseSchema?: Schema
): Promise<z.output<Schema>> => {
  const response = await baseClient(endpoint, options);

  if (!responseSchema) return response as z.output<Schema>;

  const parsedResponse = responseSchema.safeParse(response);

  if (!parsedResponse.success) {
    const error = `Api response parsing failed: ${options.method} ${endpoint} => ${JSON.stringify(parsedResponse.error.issues)}`;

    if (appConfig.env.APP_ENV === "prod") logError(error);
    else throw new Error(error);
  }

  return response as z.output<Schema>;
};

export type ApiOptions<Schema extends z.ZodTypeAny = z.ZodTypeAny> = BaseClientOptions & { schema?: Schema };

function apiGet<Schema extends z.ZodTypeAny>(endpoint: string, { schema, ...options }: ApiOptions<Schema> = {}) {
  return baseClientWithSchema(endpoint, { method: "GET", ...options, headers: options?.headers }, schema);
}

const withBody =
  (method: "POST" | "PATCH" | "PUT") =>
  async <Schema extends z.ZodTypeAny>(
    endpoint: string,
    payload: unknown = {},
    { schema, ...options }: ApiOptions<Schema> = {}
  ) =>
    baseClientWithSchema(
      endpoint,
      {
        method,
        body: JSON.stringify(payload),
        ...options,
        headers: { "Content-Type": "application/json", ...options?.headers },
      },
      schema
    );

const apiDelete = async (endpoint: string, options?: BaseClientOptions) =>
  baseClient(endpoint, { method: "DELETE", ...options, headers: options?.headers });

const apiPostFile = async (endpoint: string, payload: FormData, options?: BaseClientOptions) =>
  baseClient(endpoint, { method: "POST", body: payload, ...options });

export const api = {
  get: apiGet,
  post: withBody("POST"),
  delete: apiDelete,
  patch: withBody("PATCH"),
  put: withBody("PUT"),
  postFile: apiPostFile,
};
